import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, FileSignatureIcon, GripVerticalIcon, UsersIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';
import { CompareTable } from './CompareTable';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { Tag } from '../ui/Tag';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { baht } from '../../utils/format';
import { spring } from '../../utils/motion';
import type { LineupSlot, MusicEvent, Performer } from '../../types/marketplace';
interface LineupBoardProps {
  event: MusicEvent;
  onDiscover: () => void;
}
export function LineupBoard({
  event,
  onDiscover
}: LineupBoardProps) {
  const {
    matches,
    getPerformer,
    pencilSlot
  } = useMarketplace();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const matched = matches.filter(m => m.eventId === event.id).map(m => getPerformer(m.performerId)).filter((p): p is Performer => Boolean(p));
  const placed = new Set(event.slots.flatMap(s => [s.filledBy, s.pencilled]).filter(Boolean) as string[]);
  const tray = matched.filter(p => !placed.has(p.id));
  const booked = event.slots.filter(s => s.filledBy).length;
  const assign = (slot: LineupSlot, performerId: string) => {
    const p = getPerformer(performerId);
    pencilSlot(event.id, slot.id, performerId);
    setSelected(null);
    toast(`${p?.name} pencilled in`, {
      description: 'Send an offer to lock the slot.'
    });
  };
  return <div data-id="element-493" data-mp="3nbxf7abneje2">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-494" data-mp="3ndrpzvt7gv4f">
        <div data-id="element-495" data-mp="1gmirm1g62e72">
          <h2 className="text-lg font-bold" data-id="element-496" data-mp="1glz8ihes5jrg">Line-up</h2>
          <p className="tabular text-sm text-ink-3" data-id="element-497" data-mp="1lkkb2nhi8fj2">
            {booked} of {event.slots.length} acts booked
          </p>
        </div>
        <div className="flex gap-1.5" aria-hidden="true" data-id="element-498" data-mp="1j76y1ni1df3z">
          {event.slots.map(s => <span key={s.id} className={twMerge('h-2 w-10 rounded-full', s.filledBy ? 'bg-brand' : s.pencilled ? 'bg-brand/40' : 'bg-line')} data-id="element-499" data-mp="1f2h6ubyboope" />)}
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-id="element-500" data-mp="156w8qowdk8kx">
        {event.slots.map((slot, i) => {
        const performer = slot.filledBy ? getPerformer(slot.filledBy) : slot.pencilled ? getPerformer(slot.pencilled) : undefined;
        const isOpen = !slot.filledBy && !slot.pencilled;
        const canDrop = isOpen || Boolean(slot.pencilled);
        return <div key={slot.id} onDragOver={e => {
          if (!canDrop || slot.filledBy) return;
          e.preventDefault();
          setOver(slot.id);
        }} onDragLeave={() => setOver(null)} onDrop={e => {
          e.preventDefault();
          setOver(null);
          const id = e.dataTransfer.getData('text/plain');
          if (id && !slot.filledBy) assign(slot, id);
        }} className={twMerge('flex min-h-[176px] flex-col rounded-md border-2 p-4 transition-[border-color,background-color] duration-150', isOpen ? 'border-dashed border-line' : 'border-transparent bg-surface-2', over === slot.id && 'border-brand bg-tint', selected && isOpen && 'border-brand')} data-id="element-501" data-mp="1o5ia84vt01pd" data-mpi={i}>
              <div className="flex items-start justify-between gap-2" data-id="element-502" data-mp="3gu98eyrwm06a">
                <div data-id="element-503" data-mp="3ls19ojefuyn5">
                  <p className="text-xs font-bold text-ink-3" data-id="element-504" data-mp="3lr9cx593uuva">Slot {i + 1}</p>
                  <p className="font-bold" data-id="element-505" data-mp="3qrxeulyvgw4a">{slot.kind}</p>
                  <p className="tabular text-sm text-ink-3" data-id="element-506" data-mp="3qt8eqap75jq4">
                    {slot.setLength} min · {baht(slot.budget)}
                  </p>
                </div>
                {slot.filledBy && <Tag tone="success" icon={<CheckIcon className="h-3.5 w-3.5" data-id="element-508" data-mp="wa4v7qp2euw9" />} data-id="element-507" data-mp="wawsr72832p6">Booked</Tag>}
                {slot.pencilled && !slot.filledBy && <Tag tone="brand" data-id="element-509" data-mp="rbk36ejyvhiw">Pencilled</Tag>}
              </div>

              <AnimatePresence mode="popLayout" initial={false} data-id="element-510" data-mp="1549pkk3eqogj">
                {performer ? <motion.div key={performer.id} initial={{
              opacity: 0,
              scale: 0.96
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.96
            }} transition={spring.bouncy} className="mt-auto pt-4" data-id="element-511" data-mp="113pt7iqnrovg">
                    <div className="flex items-center gap-3" data-id="element-512" data-mp="hfgvkt6ciqok">
                      <img src={performer.photos[0]} alt="" className="h-11 w-11 rounded-full object-cover" data-id="element-513" data-mp="hhbey31yyaqj" />
                      <p className="min-w-0 flex-1 truncate font-bold" data-id="element-514" data-mp="1xva07dw8uqh6">{performer.name}</p>
                      {slot.pencilled && !slot.filledBy && <button type="button" aria-label={`Remove ${performer.name} from slot`} onClick={() => pencilSlot(event.id, slot.id, undefined)} className="grid h-8 w-8 place-items-center rounded-full text-ink-3 transition-colors duration-150 hover:bg-bg hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-515" data-mp="21abkh8udr77h">
                          <XIcon className="h-4 w-4" data-id="element-516" data-mp="2p18hzglfi1z7" />
                        </button>}
                    </div>
                    {slot.pencilled && !slot.filledBy && <Button variant="accent" size="sm" fullWidth className="mt-3" icon={<FileSignatureIcon className="h-4 w-4" data-id="element-518" data-mp="37r6se3cp8760" />} onClick={() => navigate(`/o/messages/m-${event.id}-${performer.id}`)} data-id="element-517" data-mp="2fadvcb49t0d8">
                        Send offer
                      </Button>}
                  </motion.div> : <motion.button key="empty" type="button" disabled={!selected} onClick={() => selected && assign(slot, selected)} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.15
            }} className="mt-auto grid flex-1 place-items-center rounded-sm pt-4 text-sm font-semibold text-ink-3 enabled:text-brand-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-519" data-mp="37o7wtlpd4hx4">
                    {selected ? `Tap to place ${getPerformer(selected)?.name}` : 'Drop a match here'}
                  </motion.button>}
              </AnimatePresence>
            </div>;
      })}
      </div>

      <div className="mt-8" data-id="element-520" data-mp="vfslv9ds32k6">
        <h3 className="font-bold" data-id="element-521" data-mp="veznavoz3jd4">Matched, not placed</h3>
        <p className="text-sm text-ink-3" data-id="element-522" data-mp="zhmk7b0i3uh6">Drag onto a slot — or tap a match, then tap a slot.</p>
        {tray.length === 0 ? <EmptyState className="py-8" icon={<UsersIcon className="h-7 w-7" data-id="element-524" data-mp="14ecz3v0kkk10" />} title="No spare matches" body="Swipe through applicants or discover performers to fill the remaining slots." data-id="element-523" data-mp="14gz69tjjsxip">
            <Button variant="primary" onClick={onDiscover} data-id="element-525" data-mp="bpz0pe5kjlox">
              Find performers
            </Button>
          </EmptyState> : <motion.ul layout className="mt-4 flex flex-wrap gap-2" data-id="element-526" data-mp="li4qch8clr0y">
            <AnimatePresence data-id="element-527" data-mp="ljpik076wxvn">
              {tray.map(p => <motion.li key={p.id} layout initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={spring.snappy} data-id="element-528" data-mp="3k7bvje1zwy54">
                  <button type="button" draggable onDragStart={e => {
              e.dataTransfer.setData('text/plain', p.id);
              setSelected(null);
            }} onClick={() => setSelected(s => s === p.id ? null : p.id)} aria-pressed={selected === p.id} className={twMerge('flex cursor-grab items-center gap-2 rounded-full border py-1.5 pl-1.5 pr-3 transition-colors duration-150 active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand', selected === p.id ? 'border-ink bg-ink text-bg' : 'border-line bg-surface hover:border-ink-3')} data-id="element-529" data-mp="2dmdk9d0w8vir">
                    <img src={p.photos[0]} alt="" className="h-8 w-8 rounded-full object-cover" data-id="element-530" data-mp="2qam9or7dxv1" />
                    <span className="text-sm font-bold" data-id="element-531" data-mp="7pdg0d1qghnp">{p.name}</span>
                    <GripVerticalIcon className="h-4 w-4 opacity-50" aria-hidden="true" data-id="element-532" data-mp="7oluxw5ri2bo" />
                  </button>
                </motion.li>)}
            </AnimatePresence>
          </motion.ul>}
      </div>

      <CompareTable event={event} performers={matched} data-id="element-533" data-mp="3g56wirw2dt4r" />
    </div>;
}